import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import { ListPlus } from 'lucide-react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EmptyState } from '@/shared/components/EmptyState';
import { ErrorState } from '@/shared/components/ErrorState';
import { PageHeader } from '@/shared/components/PageHeader';
import { useRoleStore } from '@/shared/session/roleStore';
import { useActingRole } from '@/shared/session/useActingRole';
import { useSession } from '@/shared/session/useSession';
import type { SubscriberListRef } from '../api/subscribersApi';
import { useMyListActions } from '../hooks/useMyListActions';
import { ListPanel } from './ListPanel';
import { ListsColumn } from './ListsColumn';
import { MyListDialogs } from './MyListDialogs';

type ListsQuery = { data: SubscriberListRef[] | undefined; isError: boolean; error: unknown; refetch: () => unknown };

type Props = {
  title: string;
  description: string;
  lists: ListsQuery;
  /** Set when an admin manages one RM's lists: new lists are that RM's. */
  owner?: { id: number; name: string } | undefined;
  above?: ReactNode;
};

/** The list open on the right, kept in the URL; otherwise the first one this account can manage. */
function useOpenList(all: SubscriberListRef[]) {
  const [params, setParams] = useSearchParams();
  const current = all.find((l) => l.id === params.get('list')) ?? all.find((l) => l.editable) ?? all[0];
  const pick = (id: string) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('list', id);
      else next.delete('list');
      return next;
    }, { replace: true });
  };
  return { current, pick };
}

/** Loading, failed, or nothing to show yet. */
function ListsState({ lists, hasOpen }: { lists: ListsQuery; hasOpen: boolean }) {
  if (lists.isError) return <ErrorState error={lists.error} onRetry={() => void lists.refetch()} />;
  if (!lists.data) return <Skeleton variant="rounded" height={360} />;
  return hasOpen ? null : <EmptyState title="No lists yet" hint="Create a list, then add people who have agreed to receive WhatsApp updates." />;
}

/** Lists on the left, grouped by owner; the open list and its people on the right. The admin's page, an RM's own
 * lists, and an admin managing one RM's lists all use this screen. */
export function ClientListsManager({ title, description, lists, owner, above }: Props) {
  const all = lists.data ?? [];
  const { current, pick } = useOpenList(all);
  const a = useMyListActions(pick, all);
  const isAdmin = useRoleStore((s) => s.role) === 'admin';
  const accountIsAdmin = useActingRole().accountRole === 'admin';
  const myName = useSession().data?.name ?? '';

  return (
    <>
      {above}
      <PageHeader title={title} description={description} actions={<Button variant="contained" startIcon={<ListPlus size={16} />} onClick={a.startNew}>New list</Button>} />
      <ListsState lists={lists} hasOpen={current !== undefined} />
      {current ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 340px) minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }}>
          <ListsColumn lists={all} selected={current.id} onSelect={pick} />
          <ListPanel
            key={current.id}
            list={current}
            actions={{
              onAdd: a.startAdd,
              onImport: a.startImport,
              onRename: () => { a.startRename(current); },
              onDelete: () => { a.remove(current); },
              onRemove: (s) => { a.removeMember(s.id, s.name); },
              removingId: a.m.remove.isPending ? a.m.remove.variables : null,
            }}
          />
        </Box>
      ) : null}
      <MyListDialogs actions={a} current={current} editable={all.filter((l) => l.editable)} isAdmin={isAdmin} accountIsAdmin={accountIsAdmin} myName={myName} fixedOwner={owner} />
      <Snackbar open={a.toast !== null} autoHideDuration={5000} onClose={a.clearToast} message={a.toast} />
    </>
  );
}
